import { getOrAssignColorIndex } from './color-registry'

// 10-color palette for yard color-by schemes (POD, Outbound Carrier, Inbound Carrier).
// Placeholder values until design finalises. Colours are deliberately distinct from the
// reserved yard colours (OCCUPANCY_*, FILTERED_OVERLAY, BORDER_RULE*) in occupancies.utils.
export const COLOR_SCHEME_PALETTE = [
  '#1565C0', // blue 800
  '#EF6C00', // orange 800
  '#2E7D32', // green 800
  '#AD1457', // pink 800
  '#6A1B9A', // purple 800
  '#00838F', // cyan 800
  '#F9A825', // yellow 800
  '#4527A0', // deep purple 800
  '#283593', // indigo 800
  '#BF360C', // deep orange 900
] as const

export const COLOR_SCHEME_PALETTE_SIZE = COLOR_SCHEME_PALETTE.length

// Sentinel value used in the planner's selection array to represent the
// "Rest" bucket — everything not in the visible top-N. Chosen to be distinct
// from any real POD code, carrier type, or stringified operator id.
export const OTHER_REST_VALUE = '__rest__'

// Neutral grey for the Rest bucket. Deliberately outside the blue-grey
// occupancy family (OCCUPANCY_PARTIAL/FULL) so a Rest-coloured slot is not
// mistaken for default occupancy at a glance.
export const OTHER_REST_COLOR = '#9E9E9E'

// djb2 hash. Kept as the L2.3 fall-back for when the persistent registry runs
// out of palette slots for a scheme (more than 10 distinct historical values).
// Collisions are accepted past that point.
export const paletteIndexFor = (value: string): number => {
  let hash = 5381
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 33) ^ value.charCodeAt(i)
  }
  // Coerce to non-negative 32-bit integer, then mod palette size.
  return (hash >>> 0) % COLOR_SCHEME_PALETTE_SIZE
}

/**
 * Resolves the palette colour for a value under a colour-by scheme.
 *
 * Resolution order:
 *   1. Rest sentinel — short-circuit to the neutral grey, ignoring scheme.
 *   2. Persistent registry (L2.3) — first-come-first-served per scheme,
 *      backed by localStorage so colours survive reloads. See
 *      `color-registry.ts` for storage layout and policy.
 *   3. Fallback to the stateless L1 djb2 hash when the registry has used up
 *      all palette slots for the scheme. Collisions are accepted at this
 *      point — that's the same trade-off L1 made unconditionally.
 *
 * The `scheme` parameter is required even for the Rest sentinel so call sites
 * don't have to special-case it (they all have a scheme in scope already).
 */
export const paletteColorFor = (scheme: string, value: string): string => {
  if (value === OTHER_REST_VALUE) return OTHER_REST_COLOR
  const assigned = getOrAssignColorIndex(scheme, value, COLOR_SCHEME_PALETTE_SIZE)
  if (assigned !== null) return COLOR_SCHEME_PALETTE[assigned]
  return COLOR_SCHEME_PALETTE[paletteIndexFor(value)]
}
